import type { SyncReason, SyncRuntime, TabStateBase } from '@pkg/sync/client'
import type { SyncClientDatabase } from '@pkg/sync/common'
import { APP_METADATA_LAST_SYNC_TS_KEY } from '@pkg/sync/common'
import { Badge, Popover, Tooltip } from 'antd'
import dayjs from 'dayjs'
import type { Kysely } from 'kysely'
import { useEffect, useMemo, useState } from 'react'
import { FaCheckCircle, FaExclamationTriangle, FaSyncAlt } from 'react-icons/fa'
import { twMerge } from 'tailwind-merge'

type SyncUiState = {
  isLeader: boolean
  isOnline: boolean
  isSyncing: boolean
  lastSyncAt: string | null
  lastSyncReason: SyncReason | null
  lastSyncCursor: string | null
  pendingOutboxCount: number | null
  lastError: { at: string; message: string } | null
}

export interface SyncStatusIndicatorProps<DB extends SyncClientDatabase, TabState extends TabStateBase> {
  runtime: SyncRuntime<DB, TabState>
  /**
   * We read `appMetadata` and outbox count directly.
   * Keep this passed from the app so this component stays web-only.
   */
  db: Kysely<DB>
  className?: string
}

async function readPendingOutboxCount(db: Kysely<any>): Promise<number> {
  // SQLite: count is returned as string/number depending on driver.
  const res = await db
    .selectFrom('message')
    .select(eb => eb.fn.countAll().as('c'))
    .executeTakeFirst()

  return Number(res?.c ?? 0)
}

async function readLastSyncCursor(db: Kysely<any>): Promise<string | null> {
  const row = await db
    .selectFrom('appMetadata')
    .selectAll()
    .where('key', '=', APP_METADATA_LAST_SYNC_TS_KEY)
    .executeTakeFirst()

  return row?.value ?? null
}

function getTrafficLightColor(state: SyncUiState): 'success' | 'warning' | 'error' | 'default' {
  if (!state.isOnline) return 'default'
  if (state.lastError) return 'error'
  if (state.isSyncing) return 'warning'
  if ((state.pendingOutboxCount ?? 0) > 0) return 'warning'
  if (state.lastSyncAt) return 'success'
  return 'default'
}

function getMainIcon(state: SyncUiState) {
  if (!state.isOnline) return <FaExclamationTriangle />
  if (state.lastError) return <FaExclamationTriangle />
  if (state.isSyncing) return <FaSyncAlt className="animate-spin" />
  if ((state.pendingOutboxCount ?? 0) > 0) return <FaSyncAlt />
  return <FaCheckCircle />
}

function formatIso(iso: string | null): string {
  if (!iso) return '—'
  return dayjs(iso).format('YYYY-MM-DD HH:mm:ss')
}

export function SyncStatusIndicator<DB extends SyncClientDatabase, TabState extends TabStateBase>(
  props: SyncStatusIndicatorProps<DB, TabState>,
) {
  const { runtime, db, className } = props

  const [state, setState] = useState<SyncUiState>(() => ({
    isLeader: runtime.isLeader(),
    isOnline: typeof navigator !== 'undefined' ? navigator.onLine : true,
    isSyncing: false,
    lastSyncAt: null,
    lastSyncReason: null,
    lastSyncCursor: null,
    pendingOutboxCount: null,
    lastError: null,
  }))

  useEffect(() => {
    const onOnline = () => setState(s => ({ ...s, isOnline: true }))
    const onOffline = () => setState(s => ({ ...s, isOnline: false }))
    window.addEventListener('online', onOnline)
    window.addEventListener('offline', onOffline)
    return () => {
      window.removeEventListener('online', onOnline)
      window.removeEventListener('offline', onOffline)
    }
  }, [])

  useEffect(() => {
    const refreshDbBits = async () => {
      try {
        const [pendingCount, cursor] = await Promise.all([readPendingOutboxCount(db), readLastSyncCursor(db)])
        setState(s => ({
          ...s,
          pendingOutboxCount: pendingCount,
          lastSyncCursor: cursor,
        }))
      } catch {
        // Best-effort: don’t surface DB read errors here.
      }
    }

    void refreshDbBits()

    const unLeader = runtime.events.on('sync.leader.changed', e => {
      setState(s => ({ ...s, isLeader: e.isLeader }))
    })

    const unSyncStarted = runtime.events.on('sync.sync.started', _e => {
      setState(s => ({ ...s, isSyncing: true, lastError: null }))
      void refreshDbBits()
    })

    const unSynced = runtime.events.on('sync.sync.completed', info => {
      setState(s => ({
        ...s,
        isSyncing: false,
        lastSyncAt: info.at,
        lastSyncReason: info.reason,
        lastSyncCursor: info.newSyncTs ?? s.lastSyncCursor,
        lastError: null,
      }))
      void refreshDbBits()
    })

    const unErr = runtime.events.on('sync.sync.error', e => {
      const message = e.error instanceof Error ? e.error.message : String(e.error)
      setState(s => ({
        ...s,
        isSyncing: false,
        lastError: { at: new Date().toISOString(), message },
      }))
    })

    return () => {
      unLeader()
      unSyncStarted()
      unSynced()
      unErr()
    }
  }, [runtime, db])

  const badgeStatus = useMemo(() => getTrafficLightColor(state), [state])
  const icon = useMemo(() => getMainIcon(state), [state])

  const title = state.isOnline ? (state.isLeader ? 'Sync (Leader)' : 'Sync (Follower)') : 'Offline'

  const details = (
    <div className="min-w-[260px] text-sm">
      <div className="flex items-center justify-between gap-4">
        <span className="text-gray-600">Role</span>
        <span className="font-medium">{state.isLeader ? 'Leader' : 'Follower'}</span>
      </div>
      <div className="flex items-center justify-between gap-4">
        <span className="text-gray-600">Online</span>
        <span className="font-medium">{state.isOnline ? 'Yes' : 'No'}</span>
      </div>
      <div className="flex items-center justify-between gap-4">
        <span className="text-gray-600">Syncing</span>
        <span className="font-medium">{state.isSyncing ? 'Yes' : 'No'}</span>
      </div>

      <div className="my-2 border-t border-gray-200" />

      <div className="flex items-center justify-between gap-4">
        <span className="text-gray-600">Last sync</span>
        <span className="font-medium">{formatIso(state.lastSyncAt)}</span>
      </div>
      <div className="flex items-center justify-between gap-4">
        <span className="text-gray-600">Reason</span>
        <span className="font-medium">{state.lastSyncReason ?? '—'}</span>
      </div>
      <div className="flex items-center justify-between gap-4">
        <span className="text-gray-600">Cursor</span>
        <span className="font-mono text-xs break-all max-w-[160px] text-right">{state.lastSyncCursor ?? '—'}</span>
      </div>
      <div className="flex items-center justify-between gap-4">
        <span className="text-gray-600">Outbox</span>
        <span className="font-medium">{state.pendingOutboxCount ?? '—'}</span>
      </div>

      {state.lastError && (
        <>
          <div className="my-2 border-t border-gray-200" />
          <div className="text-red-600 font-medium">Last error</div>
          <div className="text-xs text-gray-600">{formatIso(state.lastError.at)}</div>
          <div className="text-xs break-words">{state.lastError.message}</div>
        </>
      )}
    </div>
  )

  return (
    <Popover content={details} title={title} trigger={['hover']} placement="bottomRight">
      <Tooltip title={title} placement="bottom">
        <button
          type="button"
          className={twMerge(
            'inline-flex items-center gap-2 px-2 py-1 rounded-md hover:bg-gray-50 text-gray-700',
            className,
          )}
          aria-label="Sync status"
          onClick={() => void runtime.sync('manual')}
        >
          <Badge status={badgeStatus} />
          <span className="text-sm">{icon}</span>
        </button>
      </Tooltip>
    </Popover>
  )
}
